import * as React from 'react';
import { ChevronsUpDown, Plus, Users, Building, LucideProps } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '../../components/ui/sidebar';
import { Skeleton } from '../../components/ui/skeleton';

// Define and export props
// Define a more specific type for the logo, using React's built-in types
type LogoType = React.ForwardRefExoticComponent<LucideProps & React.RefAttributes<SVGSVGElement>> | React.ComponentType<LucideProps>;

export interface Team {
  id: string;
  name: string;
  logo?: LogoType;
  logoUrl?: string;
  subtext: string;
}

export interface TeamSwitcherProps {
  teams?: Team[] | null;
  currentTeam?: Team | null;
  isLoading?: boolean;
  onTeamSwitch?: (_teamId: string) => void;
}

export function TeamSwitcher({
  teams,
  currentTeam,
  isLoading,
  onTeamSwitch: _onTeamSwitch
}: TeamSwitcherProps) {
  const { isMobile } = useSidebar();
  const [activeTeamLocal, setActiveTeamLocal] = React.useState<Team | null>(null);

  const activeTeamToDisplay = currentTeam ?? (teams && teams.length > 0 ? teams[0] : activeTeamLocal);
  
  React.useEffect(() => {
    if (!currentTeam && teams && teams.length > 0 && teams[0] && teams[0].id !== activeTeamLocal?.id) {
      setActiveTeamLocal(teams[0]);
    } else if (!currentTeam && (!teams || teams.length === 0)) {
      setActiveTeamLocal(null);
    }
  }, [teams, currentTeam, activeTeamLocal?.id]);

  const handleTeamSelect = (team: Team) => {
    if (!currentTeam) {
      setActiveTeamLocal(team);
    }
    if (_onTeamSwitch) {
      _onTeamSwitch(team.id);
    }
  };

  if (isLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size='lg' disabled className='w-full justify-start'>
            <div className='flex w-full items-center gap-3'>
              <Skeleton className='aspect-square size-8 rounded-lg' />
              <div className='flex flex-col items-start gap-1'>
                <Skeleton className='h-4 w-24' />
                <Skeleton className='h-3 w-16' />
              </div>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!activeTeamToDisplay) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size='lg'
            className='opacity-70 cursor-default'
            disabled
          >
            <div className='flex aspect-square size-8 items-center justify-center rounded-lg bg-muted text-muted-foreground'>
              <Users className='size-4' />
            </div>
            <div className='grid flex-1 text-left text-sm leading-tight'>
              <span className='truncate font-semibold'>No Company</span>
              <span className='truncate text-xs'>N/A</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }
  
  const LogoComponent = activeTeamToDisplay.logo || Building;

  if (currentTeam && (!teams || teams.length <= 1)) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size='lg' className='w-full justify-start cursor-default'>
            <div className='flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground overflow-hidden'>
              {activeTeamToDisplay.logoUrl ? (
                <img
                  src={activeTeamToDisplay.logoUrl}
                  alt={activeTeamToDisplay.name}
                  className='size-full object-cover'
                />
              ) : (
                <LogoComponent className='size-4' />
              )}
            </div>
            <div className='grid flex-1 text-left text-sm leading-tight'>
              <span className='truncate font-semibold'>
                {activeTeamToDisplay.name}
              </span>
              <span className='truncate text-xs'>{activeTeamToDisplay.subtext}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <div className='flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground overflow-hidden'>
                {activeTeamToDisplay.logoUrl ? (
                  <img
                    src={activeTeamToDisplay.logoUrl}
                    alt={activeTeamToDisplay.name}
                    className='size-full object-cover'
                  />
                ) : (
                  <LogoComponent className='size-4' />
                )}
              </div>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>
                  {activeTeamToDisplay.name}
                </span>
                <span className='truncate text-xs'>{activeTeamToDisplay.subtext}</span>
              </div>
              <ChevronsUpDown className='ml-auto' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            align='start'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='text-xs text-muted-foreground'>
              Switch Company
            </DropdownMenuLabel>
            {(teams && teams.length > 0 ? teams : []).map((team, _index) => {
              const ItemLogoComponent: LogoType = team.logo || Building;
              return (
              <DropdownMenuItem
                  key={team.id}
                  onClick={() => handleTeamSelect(team)}
                className='gap-2 p-2'
              >
                <div className='flex size-6 items-center justify-center rounded-sm border overflow-hidden'>
                  {team.logoUrl ? (
                    <img
                      src={team.logoUrl}
                      alt={team.name}
                      className='size-full object-cover'
                    />
                  ) : (
                    <ItemLogoComponent className='size-4 shrink-0' />
                  )}
                </div>
                {team.name}
              </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem className='gap-2 p-2'>
              <div className='flex size-6 items-center justify-center rounded-md border bg-background'>
                <Plus className='size-4' />
              </div>
              <div className='font-medium text-muted-foreground'>Add team</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
